import React, { useEffect, useState } from 'react';
import { Skeleton, Empty, Tag, Button, Input, Space, Steps } from 'antd';
import { CheckOutlined, CloseOutlined, GlobalOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchMerchantDomain,
  approveMerchantDomain,
  rejectMerchantDomain,
  clearMerchantDomain,
} from '../store/merchantsUserSlice';
import { notification } from '../../../commonComponents/Notification/Notification';
import { DOMAIN_STATUS_CONFIG, DOMAIN_TYPE_LABELS, getPreferredCNAME, getPreferredIPv4List } from '../../Settings/constants';
import CopyableCode from '../../Settings/Domain/CopyableCode';
import { StyledModal } from '../styles';
import {
  DomainName,
  DnsInfoRow,
  DnsPropagationHint,
  DomainSkeletonList,
  EmptyDescription,
  DomainCardAlert,
  DomainInfoCard,
  ModalSectionTitle,
  ModalSectionDesc,
} from '../../Settings/Styles';
import type { MerchantDomainModalProps } from '../types';

const { TextArea } = Input;

const formatDate = (iso?: string | null): string | undefined =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined;

const MerchantDomainModal: React.FC<MerchantDomainModalProps> = ({ open, merchant, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { domain } = useSelector((state: RootState) => state.merchants);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (open && merchant) {
      dispatch(fetchMerchantDomain(merchant.id));
    }
  }, [open, merchant, dispatch]);

  const handleClose = () => {
    setShowRejectForm(false);
    setRejectReason('');
    dispatch(clearMerchantDomain());
    onClose();
  };

  const handleApprove = async () => {
    if (!merchant) return;
    try {
      await dispatch(approveMerchantDomain(merchant.id)).unwrap();
      notification.success('Domain Approved', `${merchant.name}'s domain has been added to Vercel.`);
    } catch (error: any) {
      notification.error(typeof error === 'string' ? error : error?.message || 'Failed to approve domain.');
    }
  };

  const handleReject = async () => {
    if (!merchant || !rejectReason.trim()) return;
    try {
      await dispatch(rejectMerchantDomain({ merchantId: merchant.id, reason: rejectReason.trim() })).unwrap();
      notification.success('Domain Rejected', `${merchant.name}'s domain request has been rejected.`);
      setShowRejectForm(false);
      setRejectReason('');
    } catch (error: any) {
      notification.error(typeof error === 'string' ? error : error?.message || 'Failed to reject domain.');
    }
  };

  const domainData = domain.data;
  const statusCfg = domainData ? DOMAIN_STATUS_CONFIG[domainData.status] : null;

  return (
    <StyledModal
      title={
        <span>
          <GlobalOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {merchant ? `Custom Domain — ${merchant.name}` : 'Custom Domain'}
        </span>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      centered
      width={480}
      styles={{ body: { padding: '16px 20px 20px' } }}
    >
      {domain.loading ? (
        <DomainSkeletonList>
          <Skeleton.Input active style={{ width: '100%', height: 60, borderRadius: 8 }} />
        </DomainSkeletonList>
      ) : !domainData ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<EmptyDescription>No domain submitted by this merchant yet.</EmptyDescription>}
        />
      ) : (
        <div>
          <DomainInfoCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <GlobalOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                <DomainName style={{ fontSize: 15 }}>{domainData.domain}</DomainName>
              </div>
              {statusCfg && (
                <Tag icon={<statusCfg.Icon />} color={statusCfg.color}>{statusCfg.label}</Tag>
              )}
            </div>
            <Space size={8} style={{ marginTop: 10 }}>
              <Tag>{DOMAIN_TYPE_LABELS[domainData.domainType] ?? domainData.domainType}</Tag>
              <span style={{ fontSize: 12, color: '#8b949e' }}>Submitted {formatDate(domainData.createdAt)}</span>
            </Space>
          </DomainInfoCard>

          {domainData.status !== 'error' && (
            <Steps
              size="small"
              style={{ margin: '20px 4px' }}
              current={
                domainData.status === 'pending_approval' ? 1 : domainData.status === 'pending_verification' ? 2 : 3
              }
              items={[
                { title: 'Submitted', description: formatDate(domainData.createdAt) },
                { title: 'Approved', description: formatDate(domainData.approvedAt) },
                { title: 'Live', description: formatDate(domainData.activatedAt) },
              ]}
            />
          )}

          <div style={{ marginTop: domainData.status === 'error' ? 20 : 4 }}>
            {domainData.status === 'pending_approval' && !showRejectForm && (
              <div>
                <ModalSectionTitle>Review this request</ModalSectionTitle>
                <ModalSectionDesc>Approve to add this domain to Vercel, or reject with a reason.</ModalSectionDesc>
                <Space.Compact block>
                  <Button
                    block
                    type="primary"
                    size="large"
                    icon={<CheckOutlined />}
                    loading={domain.actionLoading}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                  <Button
                    block
                    danger
                    size="large"
                    icon={<CloseOutlined />}
                    disabled={domain.actionLoading}
                    onClick={() => setShowRejectForm(true)}
                  >
                    Reject
                  </Button>
                </Space.Compact>
              </div>
            )}

            {domainData.status === 'pending_approval' && showRejectForm && (
              <div>
                <ModalSectionTitle>Reject this domain</ModalSectionTitle>
                <ModalSectionDesc>This reason will be shown to the merchant.</ModalSectionDesc>
                <TextArea
                  rows={3}
                  placeholder="e.g. Domain ownership could not be verified"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ marginBottom: 12 }}
                />
                <Space size={8}>
                  <Button
                    danger
                    type="primary"
                    size="large"
                    loading={domain.actionLoading}
                    disabled={!rejectReason.trim()}
                    onClick={handleReject}
                  >
                    Confirm Reject
                  </Button>
                  <Button size="large" disabled={domain.actionLoading} onClick={() => { setShowRejectForm(false); setRejectReason(''); }}>
                    Cancel
                  </Button>
                </Space>
              </div>
            )}

            {(domainData.status === 'pending_verification' || domainData.status === 'active') && (() => {
              const verificationRecord = domainData.verificationRecords?.[0];
              const cname = getPreferredCNAME(domainData.dnsRecords);
              const ipv4List = getPreferredIPv4List(domainData.dnsRecords);
              const hasRouting = domainData.domainType === 'subdomain' ? !!cname : ipv4List.length > 0;

              return (
                <DomainCardAlert
                  type={domainData.status === 'active' ? 'success' : 'warning'}
                  showIcon
                  message={domainData.status === 'active' ? 'Domain is live' : 'Waiting on merchant DNS setup'}
                  description={
                    <div>
                      {verificationRecord && (
                        <div style={{ marginBottom: 12 }}>
                          <strong>{hasRouting ? '1. ' : ''}Ownership Verification</strong>
                          <DnsInfoRow style={{ marginTop: 6 }}>
                            <span><strong>Type:</strong> {verificationRecord.type}</span>
                            <span><strong>Host:</strong> {verificationRecord.domain}</span>
                          </DnsInfoRow>
                          <strong>Value:</strong>
                          <CopyableCode value={verificationRecord.value} />
                        </div>
                      )}

                      {hasRouting && (
                        <div style={{ marginBottom: 12 }}>
                          <strong>{verificationRecord ? '2. ' : ''}Routing</strong>
                          {domainData.domainType === 'subdomain' ? (
                            <>
                              <DnsInfoRow style={{ marginTop: 6 }}>
                                <span><strong>Type:</strong> CNAME</span>
                                <span><strong>Host:</strong> {domainData.domain.split('.')[0]}</span>
                              </DnsInfoRow>
                              <strong>Value:</strong>
                              <CopyableCode value={cname!} />
                            </>
                          ) : (
                            <>
                              <DnsInfoRow style={{ marginTop: 6 }}>
                                <span><strong>Type:</strong> A</span>
                                <span><strong>Host:</strong> @</span>
                              </DnsInfoRow>
                              <strong>Value{ipv4List.length > 1 ? 's' : ''}:</strong>
                              {ipv4List.map((ip) => (
                                <CopyableCode key={ip} value={ip} />
                              ))}
                            </>
                          )}
                        </div>
                      )}

                      <DnsPropagationHint>
                        {domainData.dnsRecords?.misconfigured
                          ? 'DNS not yet detected — propagation can take up to 24–48 hours.'
                          : 'Vercel domain: ' + (domainData.vercelDomainName || domainData.domain)}
                      </DnsPropagationHint>
                    </div>
                  }
                />
              );
            })()}

            {domainData.status === 'error' && domainData.errorMessage && (
              <DomainCardAlert type="error" showIcon message="Domain error" description={domainData.errorMessage} />
            )}
          </div>
        </div>
      )}
    </StyledModal>
  );
};

export default MerchantDomainModal;
